// D-COST-4 — Background workers. SaaS-admin only (route: RequireMode saas + admin.users.read).
//
// Two controls and one read-out, in the order an admin reasons about them:
//   1. Schedule  — what a local hour MEANS. Host crons run in UTC and ask the server whether the
//                  configured hour has arrived, so this is a stored setting, not an App Service
//                  restart. An unresolvable zone is shown as a FAULT, because Azure's own
//                  WEBSITE_TIME_ZONE fails that way silently and we refuse to repeat it.
//   2. Window    — when gated workers may WAKE a tenant database. Opening an idle serverless DB
//                  blocks its auto-pause for 60 minutes, so this is the cost dial.
//   3. Deferred work — the control-plane signal ledger. Rendering it wakes NO tenant DB, which is
//                  the entire point (D-OPS-1 G5 F7: an auto-refreshing dashboard that woke every
//                  tenant would silently undo the saving it is meant to show).
import { useEffect, useState } from 'react'
import { Clock, CalendarClock, Inbox, AlertTriangle, Check, RotateCcw } from 'lucide-react'
import * as api from '../../api/client'
import SearchableSelect from '../../components/common/SearchableSelect'
import { useTableFunnels, type FunnelColumn } from '../../components/common/TableFunnels'

const MUTE = 'var(--color-text-mute, #6b7280)'
const card: React.CSSProperties = { background: 'var(--color-surface, #fff)', border: '1px solid var(--color-border, #e7e5e4)', borderRadius: 10, padding: '14px 16px', marginBottom: 14 }
const th: React.CSSProperties = { textAlign: 'left', fontSize: 10, color: MUTE, textTransform: 'uppercase', letterSpacing: '.03em', padding: '7px 7px', borderBottom: '1px solid var(--color-border, #e7e5e4)' }
const thR: React.CSSProperties = { ...th, textAlign: 'right' }
const td: React.CSSProperties = { padding: '8px 7px', fontSize: 12.5, borderBottom: '1px solid #f1f0ee', fontVariantNumeric: 'tabular-nums' }
const tdR: React.CSSProperties = { ...td, textAlign: 'right' }
const label: React.CSSProperties = { display: 'block', fontSize: 10.5, color: MUTE, textTransform: 'uppercase', letterSpacing: '.03em', marginBottom: 4 }
const input: React.CSSProperties = { width: '100%', padding: '6px 9px', fontSize: 13, border: '1px solid var(--color-border, #e7e5e4)', borderRadius: 7, background: 'var(--color-surface, #fff)' }
const badge = (bg: string, fg: string): React.CSSProperties => ({ display: 'inline-block', fontSize: 10.5, fontWeight: 800, padding: '2px 9px', borderRadius: 999, background: bg, color: fg, whiteSpace: 'nowrap' })
const btn = (primary?: boolean): React.CSSProperties => ({
  padding: '7px 14px', fontSize: 12.5, fontWeight: 600, borderRadius: 7, cursor: 'pointer',
  border: primary ? 'none' : '1px solid var(--color-border, #e7e5e4)',
  background: primary ? 'var(--color-primary, #1f2937)' : 'var(--color-surface, #fff)',
  color: primary ? '#fff' : 'inherit',
})

const DAYS = [{ n: 1, s: 'Mon' }, { n: 2, s: 'Tue' }, { n: 3, s: 'Wed' }, { n: 4, s: 'Thu' }, { n: 5, s: 'Fri' }, { n: 6, s: 'Sat' }, { n: 7, s: 'Sun' }]
const hhmm = (h: number) => `${String(h).padStart(2, '0')}:00`
// Project standard is SearchableSelect rather than a native dropdown (CI-enforced). It earns its
// keep on a 24-hour list too: typing "14" jumps straight to 14:00 instead of scrolling a whole day.
const HOUR_OPTS = Array.from({ length: 24 }, (_, i) => ({ value: String(i), label: hhmm(i) }))
// "Until" is exclusive, so 24 means end-of-day and 0 would mean an empty window.
// 0 is deliberately offered and deliberately labelled: it is the original behaviour and the
// reason a single old failure could hold a database awake indefinitely.
const AGE_OPTS = [
  { value: '0', label: 'No limit' },
  ...[1, 3, 7, 14, 30, 60, 90, 180, 365].map(d => ({ value: String(d), label: `${d} days` })),
]
const END_OPTS = Array.from({ length: 24 }, (_, i) => ({ value: String(i + 1), label: hhmm((i + 1) % 24) }))

/** "2h 15m" — a signal that never clears is a drain that is failing, so age is the headline. */
function waited(mins: number) {
  if (mins < 60) return `${mins}m`
  const h = Math.floor(mins / 60)
  return h < 24 ? `${h}h ${mins % 60}m` : `${Math.floor(h / 24)}d ${h % 24}h`
}

// D-UI-2: what each column of "Work waiting" shows, for its header funnel.
const WORK_FUNNELS: FunnelColumn<api.TenantWorkSignal>[] = [
  { key: 'pharmacy', label: 'Pharmacy',       value: w => w.name || `${w.branch_id.slice(0, 8)}…` },
  { key: 'work',     label: 'Work',           value: w => w.work_kind === 'audit_outbox' ? 'Audit trail' : w.work_kind === 'pioneer_retry' ? 'Prescription mirror' : w.work_kind },
  { key: 'signals',  label: 'Signals',        value: w => w.signal_count },
  { key: 'waiting',  label: 'Waiting',        value: w => waited(w.waiting_minutes) },
  { key: 'drained',  label: 'Last processed', value: w => w.last_drained_at },
]

export default function WorkersTab() {
  const [sched, setSched] = useState<api.WorkerSchedule | null>(null)
  const [work, setWork] = useState<api.TenantWorkSignal[] | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [saveErr, setSaveErr] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)
  const [busy, setBusy] = useState(false)

  // Draft state, so a half-typed zone id is never sent and the Save button means something.
  const [tz, setTz] = useState('')
  const [rh, setRh] = useState(6)
  const [won, setWon] = useState(false)
  const [days, setDays] = useState<number[]>([])
  const [ws, setWs] = useState(6)
  const [we, setWe] = useState(19)
  const [age, setAge] = useState(7)

  const apply = (s: api.WorkerSchedule) => {
    setSched(s); setTz(s.time_zone); setRh(s.reconcile_hour); setWon(s.window_enabled)
    setDays(s.window_days.split(',').map(Number).filter(n => n >= 1 && n <= 7))
    setWs(s.window_start_hour); setWe(s.window_end_hour)
    setAge(s.pioneer_retry_max_age_days)
  }

  const load = async () => {
    try {
      const [s, w] = await Promise.all([api.opsWorkerSchedule(), api.opsTenantWork()])
      apply(s); setWork(w.signals || []); setErr(null)
    } catch (e: any) { setErr(e?.body?.error || e?.message || 'Failed to load') }
  }
  useEffect(() => { load() }, [])

  const save = async () => {
    setBusy(true); setSaveErr(null); setSaved(false)
    try {
      // The server validates the zone and 400s an unknown id — surface that verbatim rather
      // than storing something that would silently leave workers on UTC.
      const r = await api.opsSetWorkerSchedule({
        timeZone: tz.trim(), reconcileHour: rh, windowEnabled: won,
        windowDays: days.slice().sort((a, b) => a - b).join(','),
        windowStartHour: ws, windowEndHour: we,
        pioneerRetryMaxAgeDays: age,
      })
      apply(r); setSaved(true); setTimeout(() => setSaved(false), 2500)
    } catch (e: any) {
      setSaveErr(e?.body?.hint ? `${e.body.error} — ${e.body.hint}` : (e?.body?.error || e?.message || 'Save failed'))
    } finally { setBusy(false) }
  }

  const toggleDay = (n: number) => setDays(d => d.includes(n) ? d.filter(x => x !== n) : [...d, n])

  // The header funnels filter the whole loaded ledger.
  const fx = useTableFunnels(work, WORK_FUNNELS)

  if (err) return <div style={{ padding: 20, color: '#991b1b', fontSize: 13 }}>{err}</div>
  if (!sched) return <div style={{ padding: 20, fontSize: 13, color: MUTE }}>Loading worker settings…</div>

  const dirty = tz.trim() !== sched.time_zone || rh !== sched.reconcile_hour || won !== sched.window_enabled
    || days.slice().sort((a, b) => a - b).join(',') !== sched.window_days
    || ws !== sched.window_start_hour || we !== sched.window_end_hour
    || age !== sched.pioneer_retry_max_age_days
  const noDays = won && days.length === 0

  return (
    <div style={{ padding: '18px 22px', maxWidth: 1000 }}>
      <h2 style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 18, margin: '0 0 4px' }}>
        <Clock size={18} /> Background workers
      </h2>
      <p style={{ fontSize: 12.5, color: MUTE, margin: '0 0 16px', maxWidth: 720 }}>
        Background jobs run on fixed UTC timers and ask the server what your local time is, so these
        settings take effect on the next tick — no deploy, no restart. Opening an idle tenant database
        keeps it billing for at least an hour, so the window below is the main cost control.
      </p>

      {/* ---------- 1. Schedule ---------- */}
      <div style={card}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 12 }}>
          <CalendarClock size={15} />
          <strong style={{ fontSize: 13.5 }}>Time zone &amp; daily sweep</strong>
        </div>

        {!sched.time_zone_resolved && (
          <div style={{ ...badge('#fef2f2', '#991b1b'), display: 'flex', alignItems: 'center', gap: 6, padding: '7px 11px', borderRadius: 8, marginBottom: 12, whiteSpace: 'normal' }}>
            <AlertTriangle size={13} />
            <span>Saved zone <code>{sched.time_zone}</code> is not recognised — workers are running in <strong>UTC</strong>.</span>
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 12, alignItems: 'end' }}>
          <div>
            <label style={label}>Time zone</label>
            <input style={input} value={tz} onChange={e => setTz(e.target.value)}
                   placeholder="America/New_York" spellCheck={false} />
            <div style={{ fontSize: 11, color: MUTE, marginTop: 4 }}>
              IANA (<code>America/New_York</code>) or Windows (<code>Eastern Standard Time</code>).
            </div>
          </div>
          <div>
            <label style={label}>Daily sweep at</label>
            <SearchableSelect ariaLabel="Daily sweep hour" style={input}
              value={String(rh)} options={HOUR_OPTS} onChange={v => setRh(Number(v))} />
          </div>
          <div>
            <label style={label}>Now, at the pharmacies</label>
            {/* Server-rendered in the configured zone. Formatting local_now in the browser would
                show YOUR clock — misleading for an operator in a different country to the
                pharmacies, and it would contradict the OPEN/CLOSED badge below. */}
            <div style={{ fontSize: 13, fontVariantNumeric: 'tabular-nums', padding: '6px 0' }}>
              {sched.local_now_text}
              <div style={{ fontSize: 11, color: MUTE }}>
                {sched.time_zone} · {sched.utc_now_text} UTC
              </div>
            </div>
          </div>
        </div>
        <div style={{ fontSize: 11.5, color: MUTE, marginTop: 10 }}>
          The daily sweep is a safety net that checks every pharmacy for missed background work.
          Schedule it for the <strong>start of the working day</strong> — it then reuses a database
          the pharmacy was about to open anyway instead of waking one overnight.
        </div>
      </div>

      {/* ---------- 2. Window ---------- */}
      <div style={card}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 4 }}>
          <Clock size={15} />
          <strong style={{ fontSize: 13.5 }}>Working window</strong>
          <span style={{ marginLeft: 'auto' }}>
            {sched.window_enabled
              ? (sched.in_window_now ? <span style={badge('#ecfdf5', '#065f46')}>OPEN NOW</span>
                                     : <span style={badge('#f5f5f4', '#57534e')}>CLOSED NOW</span>)
              : <span style={badge('#f5f5f4', '#57534e')}>OFF</span>}
          </span>
        </div>
        <p style={{ fontSize: 12, color: MUTE, margin: '0 0 12px', maxWidth: 720 }}>
          Outside this window, routine jobs leave tenant databases alone so they can sleep.
          Audit and prescription-mirror recovery are <strong>never</strong> paused by it — those run
          the moment there is something to do, at any hour.
        </p>

        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginBottom: 12, cursor: 'pointer' }}>
          <input type="checkbox" checked={won} onChange={e => setWon(e.target.checked)} />
          Limit routine jobs to a working window
        </label>

        <div style={{ opacity: won ? 1 : .45, pointerEvents: won ? 'auto' : 'none' }}>
          <label style={label}>Days</label>
          <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
            {DAYS.map(d => (
              <button key={d.n} type="button" onClick={() => toggleDay(d.n)}
                style={{ ...btn(days.includes(d.n)), padding: '5px 11px', fontSize: 12 }}>
                {d.s}
              </button>
            ))}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 2fr', gap: 12, alignItems: 'end' }}>
            <div>
              <label style={label}>From</label>
              <SearchableSelect ariaLabel="Window start hour" style={input}
                value={String(ws)} options={HOUR_OPTS} onChange={v => setWs(Number(v))} />
            </div>
            <div>
              <label style={label}>Until</label>
              <SearchableSelect ariaLabel="Window end hour" style={input}
                value={String(we)} options={END_OPTS} onChange={v => setWe(Number(v))} />
            </div>
            <div style={{ fontSize: 11.5, color: MUTE }}>
              {ws <= we
                ? <>Jobs run {hhmm(ws)}–{hhmm(we % 24)}; the last hour worked is {hhmm((we - 1 + 24) % 24)}.</>
                : <>Window crosses midnight: {hhmm(ws)} through {hhmm(we % 24)} the next day.</>}
            </div>
          </div>
          {noDays && (
            <div style={{ fontSize: 12, color: '#991b1b', marginTop: 10 }}>
              Pick at least one day — a window with no days would stop routine jobs entirely.
            </div>
          )}
        </div>
      </div>

      {/* ---------- 2b. Retry horizon ---------- */}
      <div style={card}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 4 }}>
          <RotateCcw size={15} />
          <strong style={{ fontSize: 13.5 }}>Prescription-mirror retry horizon</strong>
        </div>
        <p style={{ fontSize: 12, color: MUTE, margin: '0 0 12px', maxWidth: 720 }}>
          How far back to keep retrying prescriptions that failed to reach the mirror. Older ones
          are <strong>kept but skipped</strong> — nothing is deleted, and raising this brings them
          straight back. A long horizon means a pharmacy's database stays awake grinding old
          records, so this is the dial between recovering history and paying for it.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: 12, alignItems: 'end' }}>
          <div>
            <label style={label}>Retry the last</label>
            <SearchableSelect ariaLabel="Retry horizon in days" style={input}
              value={String(age)} options={AGE_OPTS} onChange={v => setAge(Number(v))} />
          </div>
          <div style={{ fontSize: 11.5, color: MUTE, paddingBottom: 6 }}>
            {age === 0
              ? 'No limit — every failed prescription is retried forever, however old.'
              : `Failures older than ${age} day${age === 1 ? '' : 's'} are left alone. They keep their data and can be brought back by raising this.`}
          </div>
        </div>
      </div>

      {/* ---------- save ---------- */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <button style={{ ...btn(true), opacity: (!dirty || busy || noDays) ? .5 : 1, cursor: (!dirty || busy || noDays) ? 'default' : 'pointer' }}
                disabled={!dirty || busy || noDays} onClick={save}>
          {busy ? 'Saving…' : 'Save changes'}
        </button>
        {saved && <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12.5, color: '#065f46' }}><Check size={14} /> Saved</span>}
        {saveErr && <span style={{ fontSize: 12.5, color: '#991b1b' }}>{saveErr}</span>}
      </div>

      {/* ---------- 3. Deferred work ---------- */}
      <div style={card}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 4 }}>
          <Inbox size={15} />
          <strong style={{ fontSize: 13.5 }}>Work waiting</strong>
          <span style={{ marginLeft: 'auto', fontSize: 11, color: MUTE }}>Reading this never wakes a database</span>
        </div>
        {work && work.length === 0 ? (
          <div style={{ fontSize: 12.5, color: MUTE, padding: '10px 0' }}>
            Nothing waiting — every pharmacy is up to date. This is the normal state.
          </div>
        ) : (
          <>
          {fx.chips}
          <table {...fx.tableProps} style={{ width: '100%', borderCollapse: 'collapse', marginTop: 8 }}>
            <thead>
              <tr>
                <th style={th}>Pharmacy {fx.funnel('pharmacy')}</th>
                <th style={th}>Work {fx.funnel('work')}</th>
                {/* signal_count, i.e. how many times work was FLAGGED (one per inbound event) —
                    not how many records are pending. Labelling it "Items" invited exactly the
                    wrong question. */}
                <th style={thR}>Signals {fx.funnel('signals')}</th>
                <th style={thR}>Waiting {fx.funnel('waiting')}</th>
                <th style={thR}>Last processed {fx.funnel('drained')}</th>
              </tr>
            </thead>
            <tbody>
              {fx.rows.map(w => {
                // Anything queued for over an hour has survived at least four normal passes,
                // so it is a stuck drain rather than a busy moment.
                const stuck = w.waiting_minutes > 60
                return (
                  <tr key={`${w.branch_id}:${w.work_kind}`}>
                    <td style={td}>{w.name || <span style={{ color: MUTE }}>{w.branch_id.slice(0, 8)}…</span>}</td>
                    <td style={td}>{w.work_kind === 'audit_outbox' ? 'Audit trail' : w.work_kind === 'pioneer_retry' ? 'Prescription mirror' : w.work_kind}</td>
                    <td style={tdR}>{w.signal_count.toLocaleString()}</td>
                    <td style={tdR}>
                      {stuck ? <span style={badge('#fef2f2', '#991b1b')}>{waited(w.waiting_minutes)}</span> : waited(w.waiting_minutes)}
                    </td>
                    <td style={tdR}>{w.last_drained_at ? new Date(w.last_drained_at).toLocaleString() : '—'}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
          </>
        )}
      </div>
    </div>
  )
}
